/**
 * ORBAT (Order of Battle) related TypeScript type definitions.
 * Includes types for Factions, Unit Groups, and Units used in the ORBAT Creator.
 */

// =============================================================================
// Faction Types
// =============================================================================

export interface Faction {
    id: string;
    name: string;
    event_id: string;
    affiliation_id: string;
    created_at: string;
    updated_at: string;
}

export interface FactionCreate {
    name: string;
    affiliation_id: string;
}

export interface FactionUpdate {
    name?: string;
    affiliation_id?: string;
}

export interface FactionListResponse {
    factions: Faction[];
    total: number;
}

// =============================================================================
// Unit Group Types
// =============================================================================

export interface UnitGroup {
    id: string;
    name: string;
    event_id: string;
    faction_id: string;
    root_unit_id: string | null;
    created_at: string;
    updated_at: string;
}

export interface UnitGroupCreate {
    name: string;
    faction_id: string;
}

export interface UnitGroupUpdate {
    name?: string;
    faction_id?: string;
}

export interface UnitGroupListResponse {
    unit_groups: UnitGroup[];
    total: number;
}

// =============================================================================
// Unit Types
// =============================================================================

export interface Unit {
    id: string;
    name: string;
    sidc: string;
    faction_id: string;
    unit_group_id: string;
    upper_unit_id: string | null;
    unit_order: number;
    /**
     * Whether this unit can be claimed by public players via the in-app lobby.
     * Optional on the wire — older backend deploys or partial responses may omit
     * the field. Readers must explicitly coerce to `false` at the read site.
     */
    is_publicly_joinable?: boolean;
    /** milsymbol SymbolOptions overlay (uniqueDesignation, additionalInformation, …). */
    text_modifiers?: Record<string, unknown>;
    created_at: string;
    updated_at: string;
    latitude: number | null;
    longitude: number | null;
    position_time: string | null;
}

export interface UnitCreate {
    name: string;
    sidc: string;
    faction_id: string;
    unit_group_id?: string;
    upper_unit_id?: string | null;
    unit_order?: number;
    is_publicly_joinable?: boolean;
    /** Text-modifier overlay. Strip the dynamic `dtg` before sending. */
    text_modifiers?: Record<string, unknown>;
}

export interface UnitUpdate {
    name?: string;
    sidc?: string;
    upper_unit_id?: string | null;
    unit_order?: number;
    is_publicly_joinable?: boolean;
    /**
     * Full-replacement text-modifier map: present = replace stored map;
     * omit/undefined = leave unchanged; `{}` = clear. Strip `dtg` first.
     */
    text_modifiers?: Record<string, unknown>;
}

export interface UnitPositionUpdate {
    upper_unit_id: string | null;
    unit_order: number;
}

export interface UnitListResponse {
    units: Unit[];
    total: number;
}

// =============================================================================
// Tree Structure Types
// =============================================================================

export interface UnitTreeNode {
    id: string;
    name: string;
    sidc: string;
    unit_order: number;
    /** milsymbol SymbolOptions overlay stored on the unit. */
    text_modifiers?: Record<string, unknown>;
    /**
     * Whether this unit can be claimed by public players via the in-app lobby.
     * Optional on the wire — older backend deploys or partial responses may omit
     * the field. Readers must explicitly coerce to `false` at the read site.
     */
    is_publicly_joinable?: boolean;
    children: UnitTreeNode[];
}

export interface UnitGroupTreeResponse {
    unit_group: UnitGroup;
    tree: UnitTreeNode | null;
}

/** Where a dragged unit would land relative to a hovered sibling. */
export interface InsertionIndicator {
    nodeId: string;
    edge: 'before' | 'after';
}

// =============================================================================
// Redux State Types
// =============================================================================

export interface OrbatState {
    // Affiliations (reference data)
    affiliations: AffiliationType[];
    affiliationsLoading: boolean;
    affiliationsError: string | null;

    // Factions
    factions: Faction[];
    selectedFactionId: string | null;
    factionsLoading: boolean;
    factionsError: string | null;

    // Unit Groups
    unitGroups: UnitGroup[];
    selectedUnitGroupId: string | null;
    unitGroupsLoading: boolean;
    unitGroupsError: string | null;

    // Unit Tree (for selected unit group)
    selectedUnitGroupTree: UnitGroupTreeResponse | null;
    treeLoading: boolean;
    treeError: string | null;

    // General
    isLoading: boolean;
    error: string | null;
}

// =============================================================================
// Affiliation Types (for faction symbol rendering)
// =============================================================================

export interface AffiliationType {
    id: string;
    name: string;
}

export interface AffiliationTypeListResponse {
    affiliations: AffiliationType[];
}

// Map affiliation names to SIDC identity digits (position 3 in SIDC 2525D)
export const AFFILIATION_SIDC_MAP: Record<string, string> = {
    'FRIENDLY': '3',
    'HOSTILE': '6',
    'OPFOR': '6',
    'NEUTRAL': '4',
    'UNKNOWN': '1'
};

// Helper to get SIDC digit from affiliation name
export function getAffiliationSidcDigit(name: string): string {
    return AFFILIATION_SIDC_MAP[name.toUpperCase()] || '1';
}

// =============================================================================
// Drag and Drop Types
// =============================================================================

export interface DragItem {
    id: string;
    type: 'unit';
    parentId: string | null;
    index: number;
}

export interface DropResult {
    unitId: string;
    newParentId: string | null;
    newIndex: number;
}
